import { mutationNamespace, queryNamespace } from '@libs/shared/frontend/helpers/query-namespace.helper';
import { mutationOptions, queryOptions } from '@tanstack/react-query';

import { client, HttpException, type components, type operations } from '@/libs/openapi';
import { queryClient } from '@/main';
import { auditLogQueries } from '@/resources/audit-log/audit-log.api';
import { fromApiCampaignRequestFieldAdapter } from '@/resources/campaign-request/campaign-request.field-adapter';
import { fromApiUserFieldAdapter } from '@/resources/user/user.field-adapter';
import type { QueryResultData } from '@/types/query-result-data.type';
import { Resource } from '@/types/resource.type';

export const campaignRequestQueries = queryNamespace(Resource.campaignRequest, {
  list: (params?: {
    filter?: Omit<operations['CampaignRequestController_getMany']['parameters']['query'], 'limit' | 'page' | 'sortBy'>;
    sorting?: {
      id: string;
      desc: boolean;
    };
    paging: {
      pageIndex: number;
      pageSize: number;
    };
  }) => queryOptions({
    queryKey: [ params ],
    queryFn: async ({ signal }) =>
      client
        .GET('/customer/api/tcd/campaign-requests', {
          signal,
          params: {
            query: {
              limit: params?.paging?.pageSize ?? 10,
              page: (params?.paging?.pageIndex ?? 0) + 1,
              sortBy: params?.sorting
                ? [ `${params.sorting.id}:${params.sorting.desc ? 'DESC' : 'ASC'}` ]
                : undefined,
              ...params?.filter,
            },
          },
        })
        .then(({ data, error, response }) => {
          if (error) {
            throw new HttpException({
              message: Array.isArray(error.message) ? error.message[0] : error.message,
              response,
              isGlobal: false,
            });
          }

          return {
            data: data.items,
            totalCount: data.totalCount,
          };
        }),
    select: (data) => ({
      totalCount: data.totalCount,
      data: data.data.map((row) => ({
        original: row,
        transformed: {
          forLabels: fromApiCampaignRequestFieldAdapter.transform(row),
        },
        meta: {
          ownerFullName: row.owner && fromApiUserFieldAdapter.getFullName(row.owner),
          startDateRelativeToTimezone: fromApiCampaignRequestFieldAdapter.getStartDateRelativeToTimezone(row),
          endDateRelativeToTimezone: fromApiCampaignRequestFieldAdapter.getEndDateRelativeToTimezone(row),
        },
      })),
    }),
  }),
  detail: (id: number) => queryOptions({
    queryKey: [ id ],
    queryFn: async ({ signal }) =>
      client
        .GET('/customer/api/tcd/campaign-requests/{id}', {
          signal,
          params: { path: { id }},
        })
        .then(({ data, error, response }) => {
          if (error) {
            throw new HttpException({
              message: Array.isArray(error.message) ? error.message[0] : error.message,
              response,
              isGlobal: false,
            });
          }
          return data;
        }),
    select: (data) => ({
      original: data,
      transformed: {
        forLabels: fromApiCampaignRequestFieldAdapter.transform(data),
        forForm: fromApiCampaignRequestFieldAdapter.transform(data, 'forFormValue'),
      },
      meta: {
        ownerFullName: data.owner && fromApiUserFieldAdapter.getFullName(data.owner),
        createdByFullName: data.createdByOrganizationUser && fromApiUserFieldAdapter.getFullName(data.createdByOrganizationUser),
        startDateRelativeToTimezone: fromApiCampaignRequestFieldAdapter.getStartDateRelativeToTimezone(data),
        endDateRelativeToTimezone: fromApiCampaignRequestFieldAdapter.getEndDateRelativeToTimezone(data),
      },
    }),
  }),
  checkDuplicate: (params: { field: 'campaignName' | 'customId'; value: string; excludeId?: number }) =>
    queryOptions({
      queryKey: [ params ],
      queryFn: ({ signal }) =>
        client
          .GET('/customer/api/tcd/campaign-requests/check-duplicate', {
            signal,
            params: { query: params },
          })
          .then(({ data, error, response }) => {
            if (error) {
              throw new HttpException({
                message: Array.isArray(error.message) ? error.message[0] : error.message,
                response,
                isGlobal: false,
              });
            }
            return data;
          }),
    }),
});

export type CampaignRequestListQueryResultData = QueryResultData<typeof campaignRequestQueries.list>['data'][number];

export const campaignRequestMutations = mutationNamespace(Resource.campaignRequest, {
  create: mutationOptions({
    mutationFn: (body: components['schemas']['CreateCampaignRequestDto']) =>
      client
        .POST('/customer/api/tcd/campaign-requests', { body })
        .then(({ data, error, response }) => {
          if (error) {
            throw new HttpException({
              message: Array.isArray(error.message) ? error.message[0] : error.message,
              response,
              isGlobal: false,
            });
          }
          return data;
        }),
  }),
  update: mutationOptions({
    mutationFn: ({ id, body }: { id: number; body: components['schemas']['UpdateCampaignRequestDto'] }) =>
      client
        .PATCH('/customer/api/tcd/campaign-requests/{id}', { params: { path: { id }}, body })
        .then(({ error, response }) => {
          if (error) {
            throw new HttpException({
              message: Array.isArray(error.message) ? error.message[0] : error.message,
              response,
              isGlobal: false,
            });
          }
        }),
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: campaignRequestQueries.all.queryKey });
      queryClient.invalidateQueries({ queryKey: auditLogQueries.all.queryKey });
    },
  }),
  archive: mutationOptions({
    mutationFn: ({ id, archived }: { id: number; archived: boolean }) =>
      client
        .POST('/customer/api/tcd/campaign-requests/{id}/archive', { params: { path: { id }}, body: { archived }})
        .then(({ error, response }) => {
          if (error) {
            throw new HttpException({
              message: Array.isArray(error.message) ? error.message[0] : error.message,
              response,
              isGlobal: false,
            });
          }
        }),
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: campaignRequestQueries.all.queryKey });
      queryClient.invalidateQueries({ queryKey: auditLogQueries.all.queryKey });
    },
  }),
  submit: mutationOptions({
    mutationFn: (id: number) =>
      client
        .POST('/customer/api/tcd/campaign-requests/{id}/submit', { params: { path: { id }}})
        .then(({ error, response }) => {
          if (error) {
            throw new HttpException({
              message: Array.isArray(error.message) ? error.message[0] : error.message,
              response,
              isGlobal: false,
            });
          }
        }),
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: auditLogQueries.all.queryKey });
    },
  }),
});
